import { FC, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { Box, Tooltip } from "@mui/material";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers-pro";
import useBasePath from "src/utils/hooks/useBasePath";
import findInAbsenceRequestDays from "src/utils/Company/Absences/findInAbsenceRequestDays";
import { IAbsenceRequestDay } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { getAbsenceRequestDayStyles } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/Datepicker.styles";
import PlannedDayBadge from "src/Pages/Company/Absences/Calendars/Calendar/PlannedDayBagde";
import { getPickerStyles } from "./CalendarPreview.styles";

interface ICustomCalendarPreviewDayProps extends PickersDayProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  plannedDays?: string[];
  disabled: boolean;
}

const CustomCalendarPreviewDay: FC<ICustomCalendarPreviewDayProps> = ({
  absenceRequestDays = [],
  excludedDays = [],
  plannedDays,
  disabled = false,
  ...props
}) => {
  const { day, outsideCurrentMonth } = props as PickersDayProps;
  const history = useHistory();
  const basePath = useBasePath();
  const requestDay = findInAbsenceRequestDays(day, absenceRequestDays);
  const isRequestDay = !!requestDay && requestDay.title;
  const parsedToISO = format(new Date(day), "yyyy-MM-dd");
  const excludedDay = excludedDays.find((item) => item.date === parsedToISO);
  const plannedDaysSet = useMemo(() => new Set(plannedDays), [plannedDays]);
  const isPlannedDay = plannedDaysSet.has(parsedToISO);

  if (excludedDay?.label && !isRequestDay) {
    return (
      <Tooltip title={excludedDay.label} placement="top" arrow>
        <Box component="span">
          <PickersDay
            {...props}
            disabled
            sx={getPickerStyles({
              isRequestDay: false,
              isExcludedDay: false,
              color: "text.primary",
              bgColor: "transparent",
            })}
            data-testid="absences-calendar-day__excluded"
          >
            {format(day, "d")}
          </PickersDay>
        </Box>
      </Tooltip>
    );
  }

  const dayContent = (
    <PickersDay
      {...props}
      disableRipple
      disabled={disabled}
      onClick={() => {
        if (isRequestDay && requestDay.absenceRequestId) {
          history.push(
            `${basePath}/absences/absence-request/${requestDay.absenceRequestId}/preview`
          );
        }
      }}
      sx={getPickerStyles({
        isRequestDay: !!isRequestDay,
        isExcludedDay: !!excludedDay,
        color: requestDay?.color,
        bgColor: requestDay?.bgColor,
        position: requestDay?.position,
      })}
      data-testid="absences-calendar-picker-day"
    >
      {isRequestDay ? (
        <Tooltip title={requestDay.title} placement="top" arrow>
          <Box
            component="span"
            sx={getAbsenceRequestDayStyles(requestDay?.color)}
          >
            {format(day, "d")}
          </Box>
        </Tooltip>
      ) : (
        format(day, "d")
      )}
    </PickersDay>
  );

  return isPlannedDay && !outsideCurrentMonth ? (
    <PlannedDayBadge data-testid="absences-calendar-day__planned">
      {dayContent}
    </PlannedDayBadge>
  ) : (
    dayContent
  );
};

export default CustomCalendarPreviewDay;
